import React from 'react';
import { Badge } from '@roger/ui-components';
import type { PayerResponseReason } from '../../../services/api/types.gen';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import { formatReason, sourceLabel } from './payerReasonLabels';

/**
 * The ordered reasons the payer (or Stedi, before the payer) gave. One list for
 * the drawer and for the claim worksheet, so a claim and an NOA read the same.
 */
export const PayerReasonList: React.FC<{
  reasons: PayerResponseReason[];
  payerClaimNumber?: string | null;
  /** Names the thing the payer refused, for the no-reason fallback line. */
  kind?: 'claim' | 'noa';
}> = ({ reasons, payerClaimNumber, kind = 'claim' }) => {
  if (reasons.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>No reason received yet.</p>
    );
  }

  return (
    <ol className='space-y-3'>
      {reasons.map((reason, index) => {
        const { text, hint } = formatReason(reason, payerClaimNumber, kind);
        return (
          <li key={index} className='min-w-0 space-y-1'>
            <p className='text-sm text-foreground'>{text}</p>
            {hint ? (
              <p className='text-xs text-muted-foreground'>{hint}</p>
            ) : null}
            <div className='flex flex-wrap items-center gap-2'>
              <Badge variant='secondary' className='whitespace-nowrap'>
                {sourceLabel(reason.source)}
              </Badge>
              {reason.received_on ? (
                <span className='text-xs text-muted-foreground'>
                  {formatDateForDisplay(reason.received_on)}
                </span>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default PayerReasonList;
